import React, { useState, useRef, useEffect } from 'react';
import SilentAudioWarningModal from './SilentAudioWarningModal';
import ConfirmEndModal from './ConfirmEndModal'; // Import the new modal
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faMicrophoneSlash, faSquare, faVideo, faVideoSlash, faCommentSlash } from '@fortawesome/free-solid-svg-icons';
import WebcamCapture from './WebcamCapture';
import TimeWarningModal from './TimeWarningModal';

// iOS/Safari Detection Utility
const isIOSDevice = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// Get supported MIME type for MediaRecorder
const getSupportedMimeType = () => {
  const types = [
    'audio/mp4',
    'audio/aac',
    'audio/mpeg',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus'
  ];
  
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      console.log('Using MIME type:', type);
      return type;
    }
  }
  
  console.warn('No supported MIME type found, using default');
  return '';
};

// Audio Worklet Code
const silenceDetectorWorklet = `
class SilenceDetector extends AudioWorkletProcessor {
  constructor() {
    super();
    this.silenceStart = null;
    this.SILENCE_THRESHOLD = 0.01;
    this.SILENCE_DURATION = 1500;
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    const currentTime = this.currentTime;
    if (input.length > 0) {
      const samples = input[0];
      const sum = samples.reduce((acc, val) => acc + Math.abs(val), 0);
      const average = sum / samples.length;

      if (average < this.SILENCE_THRESHOLD) {
        if (this.silenceStart === null) {
          this.silenceStart = currentTime;
        } else if (currentTime - this.silenceStart > this.SILENCE_DURATION / 1000) {
          this.port.postMessage('silence-detected');
          return false;
        }
      } else {
        this.silenceStart = null;
      }
    }
    return true;
  }
}

registerProcessor('silence-detector', SilenceDetector);
`;


const SpinningArc = ({ isActive }) => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    style={{
      position: 'absolute',
      top: '0',
      left: '0',
      animation: isActive ? 'spin 2s linear infinite' : 'none',
      pointerEvents: 'none',
    }}
  >
    <circle
      cx="41"
      cy="41"
      r="38"
      fill="none"
      stroke="white"
      strokeWidth="2"
      strokeDasharray="60 180"
    />
  </svg>
);

const EnhancedAudioRecorder = ({ personaName, conversation_history, personaType, conversation_id, voice, onHistoryUpdate, user, onEmotionalStateChange, onResponseReceived, onEndConversation, time_limit, medilistValue, onLatestResponse }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isChatbotSpeaking, setIsChatbotSpeaking] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isWorkletLoaded, setIsWorkletLoaded] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [showCallout, setShowCallout] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showTooltip, setShowTooltip] = useState({ mic: false, video: false, stop: false });
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const timerRef = useRef(null);
  const currentAudioRef = useRef(null);
  const [calloutOpacity, setCalloutOpacity] = useState(0);
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  const [showSilentAudioWarning, setShowSilentAudioWarning] = useState(false); // State to control the modal
  const [isRecordButtonEnabled, setIsRecordButtonEnabled] = useState(true);
  const [showConfirmEndModal, setShowConfirmEndModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(() => {
    console.log("Setting initial remainingTime with time_limit:", time_limit);
    return time_limit;
  });  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);


  useEffect(() => {
    console.log("Received time_limit prop:", time_limit);
    if (typeof time_limit === 'number' && !isNaN(time_limit) && time_limit > 0) {
      console.log("Setting remainingTime to:", time_limit);
      setRemainingTime(time_limit);
    } else {
      console.error("Invalid time_limit received:", time_limit);
      // Set a default value or handle the error
    }
  }, [time_limit]);

  useEffect(() => {
    let timer;
    if (isTimerRunning && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime === 180) {
            setShowTimeWarning(true);
          }
          if (prevTime <= 1) {
            clearInterval(timer);
            setIsTimerRunning(false);
            handleTimerEnd();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
  
    return () => {
      clearInterval(timer);
    };
  }, [isTimerRunning, remainingTime, time_limit]);

  const handleTimerEnd = () => {
    setIsConversationEnded(true);
    onEndConversation();
  };

  useEffect(() => {
    console.log("Initial time_limit:", time_limit);
    console.log("Initial remainingTime:", remainingTime);
  }, []);

  useEffect(() => {
    console.log("time_limit changed, updating remainingTime:", time_limit);
    setRemainingTime(time_limit*60);
  }, [time_limit]);

  const handleCloseSilentWarning = () => {
    setShowSilentAudioWarning(false);
    setIsRecordButtonEnabled(true);
    setIsRecording(false);
    setIsWaitingForResponse(false);
  };

  useEffect(() => {
    const loadWorkletModule = async () => {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Only try to load AudioWorklet on non-iOS devices
        if (audioContextRef.current.audioWorklet && !isIOSDevice()) {
          const workletBlob = new Blob([silenceDetectorWorklet], { type: 'application/javascript' });
          const workletBlobUrl = URL.createObjectURL(workletBlob);
          
          try {
            await audioContextRef.current.audioWorklet.addModule(workletBlobUrl);
            console.log('AudioWorklet loaded successfully');
            setIsWorkletLoaded(true);
          } catch (error) {
            console.warn('AudioWorklet loading failed, will use fallback:', error);
          } finally {
            URL.revokeObjectURL(workletBlobUrl);
          }
        } else {
          console.log('iOS/Safari detected, will use ScriptProcessorNode');
          setIsWorkletLoaded(true); // Set to true to allow recording
        }
      } catch (error) {
        console.error('Error initializing AudioContext:', error);
        setIsWorkletLoaded(true); // Still allow recording attempt
      }
    };

    loadWorkletModule();

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const calloutTimer = setInterval(() => {
      setShowCallout(prev => !prev);
    }, 3000); // Toggle every 3 seconds

    return () => clearInterval(calloutTimer);
  }, []);



  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [isTimerRunning]);



  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsWaitingForResponse(true);
      setIsTimerRunning(false);
    }
  };


  const toggleRecording = async () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    if (isRecording) {
      stopRecording();
    } else {
      // setRemainingTime(prevTime => {
      //   console.log("Resetting timer to time_limit:", time_limit);
      //   return time_limit * 60;
      // });
            await startRecording();
      setIsTimerRunning(true);
    }
  };



  const startRecording = async () => {
    try {
      // First check if we have permission
      if (navigator.permissions && navigator.permissions.query) {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
        
        if (permissionStatus.state === 'denied') {
          throw new Error('Microphone permission has been denied. Please enable it in your browser settings.');
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Setup audio monitoring for silence detection
      if (audioContextRef.current.audioWorklet && !isIOSDevice()) {
        // Use AudioWorklet for modern browsers
        try {
          const source = audioContextRef.current.createMediaStreamSource(stream);
          workletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'silence-detector');
          
          workletNodeRef.current.port.onmessage = (event) => {
            if (event.data === 'silence-detected') {
              console.log('Silence detected by worklet');
              stopRecording();
            }
          };
          
          source.connect(workletNodeRef.current);
          workletNodeRef.current.connect(audioContextRef.current.destination);
        } catch (error) {
          console.warn('AudioWorklet setup failed, continuing without silence detection:', error);
        }
      } else {
        // Fallback to ScriptProcessorNode for iOS/older Safari
        console.log('Using ScriptProcessorNode fallback for iOS/Safari');
        try {
          const source = audioContextRef.current.createMediaStreamSource(stream);
          const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
          
          let silenceStart = null;
          const SILENCE_THRESHOLD = 0.01;
          const SILENCE_DURATION = 1500;
          
          processor.onaudioprocess = (event) => {
            const input = event.inputBuffer.getChannelData(0);
            const sum = input.reduce((acc, val) => acc + Math.abs(val), 0);
            const average = sum / input.length;
            
            if (average < SILENCE_THRESHOLD) {
              if (silenceStart === null) {
                silenceStart = Date.now();
              } else if (Date.now() - silenceStart > SILENCE_DURATION) {
                console.log('Silence detected by ScriptProcessorNode');
                processor.disconnect();
                source.disconnect();
                stopRecording();
              }
            } else {
              silenceStart = null;
            }
          };
          
          source.connect(processor);
          processor.connect(audioContextRef.current.destination);
        } catch (error) {
          console.warn('ScriptProcessorNode setup failed, continuing without silence detection:', error);
        }
      }

      // Create MediaRecorder with supported MIME type
      const mimeType = getSupportedMimeType();
      const options = mimeType ? { mimeType } : {};
      
      try {
        mediaRecorderRef.current = new MediaRecorder(stream, options);
      } catch (error) {
        console.error('Failed to create MediaRecorder with options:', options, error);
        // Fallback to default MediaRecorder without options
        mediaRecorderRef.current = new MediaRecorder(stream);
      }
      
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Use the same MIME type that was used for recording
        const recordedMimeType = mediaRecorderRef.current.mimeType || getSupportedMimeType() || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: recordedMimeType });
        console.log('Audio blob created with type:', recordedMimeType, 'size:', audioBlob.size);
        sendAudioToServer(audioBlob);
        
        // Stop all tracks to release the microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setIsTimerRunning(true);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to access microphone. ';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow microphone access in your browser settings.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found. Please check your device settings.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Your microphone is busy or not responding. Please try again.';
      }
      
      // Set error state to display to user (you'll need to add this state and UI element)
      setIsRecordButtonEnabled(false);
      alert(errorMessage); // Consider using a more user-friendly notification system
      
      // Reset recording state
      setIsRecording(false);
      setIsTimerRunning(false);
    }
  };



  const sendAudioToServer = async (audioBlob) => {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

    const isMostlySilent = isAudioMostlySilent(audioBuffer);

    if (isMostlySilent) {
      console.warn('Audio is mostly silent. Showing warning.');
      setShowSilentAudioWarning(true);
      setIsRecordButtonEnabled(false);
      setIsTimerRunning(false);
      setIsRecording(false);
      return;
    }

    // Existing code to send the audio to the server...
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    formData.append('persona', personaName);
    formData.append('conversation_history', conversation_history);
    formData.append('personaType', personaType);
    formData.append('conversation_id', conversation_id);
    formData.append('voice', voice);
    formData.append('user', user);

    try {
      const response = await fetch('https://www.nump0.com/persona/conversation_react', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let persona_text = '';
      let student_text = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n');
        while (boundary !== -1) {
          const line = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 1);
          boundary = buffer.indexOf('\n');

          if (line.trim()) {
            try {
              const data = JSON.parse(line);
              const result = handleServerResponse(data);

              if (typeof result.delta === 'string' && result.delta.length > 0) {
                persona_text += result.delta;
                if (onLatestResponse) {
                  onLatestResponse({ speaker: personaName, text: persona_text });
                }
              }

              if (typeof result.complete === 'string') {
                persona_text = result.complete;
                if (onLatestResponse) {
                  onLatestResponse({ speaker: personaName, text: persona_text });
                }
              }

              if (result.transcription !== undefined) {
                student_text = result.transcription;
              }

              if (result.audio && !isPlayingRef.current) {
                playNextAudio();
              }
            } catch (error) {
              console.error('Error parsing JSON:', error);
            }
          }
        }
      }
      const finalPersonaText = persona_text.trim();
      onResponseReceived(conversation_id, finalPersonaText);

      onHistoryUpdate(conversation_id, student_text, finalPersonaText);
      if (!isPlayingRef.current) {
        playNextAudio();
      }
    } catch (error) {
      console.error('Error sending audio to server:', error);
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  const isAudioMostlySilent = (audioBuffer) => {
    const channelData = audioBuffer.getChannelData(0); // Assuming mono audio
    const threshold = 0.01; // Define a threshold for silence
    const silentSamples = channelData.filter(sample => Math.abs(sample) < threshold).length;
    const silentRatio = silentSamples / channelData.length;

    // Consider the audio silent if more than 90% of samples are below the threshold
    return silentRatio > 0.9;
  };


  const handleServerResponse = (data) => {
    console.log('Server response:', data);

    switch (data.type) {
      case 'text_delta': {
        const delta = typeof data.content === 'string' ? data.content : '';
        onEmotionalStateChange(data.emotion);
        return { delta };
      }
      case 'text_complete': {
        const content = typeof data.content === 'string' ? data.content : '';
        onEmotionalStateChange(data.emotion);
        return { complete: content };
      }
      case 'audio': {
        if (data.content) {
          storeAudio(data.content);
        }
        return { audio: true };
      }
      case 'transcription':
        return { transcription: data.content || '' };
      default:
        return {};
    }
  };


  const storeAudio = (base64Audio) => {
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    audio.preload = 'auto';  // OPTIMIZED: Preload audio immediately to reduce playback delay
    audio.playbackRate = playbackRate;
    audioQueueRef.current.push(audio);
  };


  const playNextAudio = () => {
    if (audioQueueRef.current.length > 0) {
      isPlayingRef.current = true;
      setIsChatbotSpeaking(true);
      setIsTimerRunning(true); // Resume the timer when audio starts playing
      const audio = audioQueueRef.current.shift();
      audio.playbackRate = playbackRate;
      currentAudioRef.current = audio;
      audio.play();
      audio.onended = () => {
        setIsChatbotSpeaking(false);
        setIsTimerRunning(true); // Pause the timer when audio finishes
        playNextAudio();
      };
    } else {
      isPlayingRef.current = false;
      setIsChatbotSpeaking(false);
      setIsTimerRunning(true); // Ensure timer is paused when all audio is finished
    }
  };


  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  useEffect(() => {
    audioQueueRef.current.forEach(audio => {
      audio.playbackRate = playbackRate;
    });
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const toggleVideo = () => {
    setIsVideoActive(prev => !prev);
  };

  const handleEndConversation = () => {
    setIsTimerRunning(false);
    setShowConfirmEndModal(true);
  };

  const confirmEndConversation = () => {
    setIsConversationEnded(true);
    setIsTimerRunning(false);
    
    // Trigger any final form data collection
    if (window.medilistFormRef && window.medilistFormRef.collectFinalData) {
      window.medilistFormRef.collectFinalData();
    }
    
    // Add timestamp when conversation ended
    console.log(`[${new Date().toISOString()}] 🏁 Conversation ended by user`);
    
    // Call the parent's endConversation callback
    onEndConversation();
    setShowConfirmEndModal(false);
  };

  // Touch event handlers for better mobile/tablet support
  const handleTouchTooltip = (buttonType, show) => {
    if (isIOSDevice()) {
      setShowTooltip(prev => ({ ...prev, [buttonType]: show }));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <p className="timer">Time Remaining: {formatTime(remainingTime)}</p>
      <div className="flex space-x-4">
        <div className="relative group">
          <button
            className={`control-button mic-button ${(!isRecordButtonEnabled || isChatbotSpeaking || isWaitingForResponse || isConversationEnded) ? 'pointer-events-none opacity-50' : ''}`}
            onClick={toggleRecording}
            disabled={!isRecordButtonEnabled || isChatbotSpeaking || isWaitingForResponse || isConversationEnded}
            onMouseEnter={() => setShowTooltip(prev => ({ ...prev, mic: true }))}
            onMouseLeave={() => setShowTooltip(prev => ({ ...prev, mic: false }))}
            onTouchStart={() => handleTouchTooltip('mic', true)}
            onTouchEnd={() => handleTouchTooltip('mic', false)}
          >
            <FontAwesomeIcon
              icon={isRecording ? faSquare : (isChatbotSpeaking || isWaitingForResponse ? faMicrophoneSlash : faMicrophone)}
              className="icon"
            />
            <span className="label">{isRecording ? "Stop" : "Start Message"}</span>
          </button>
          {showTooltip.mic && (
            <div className="custom-tooltip">
              {isRecording ? "Stop recording" : "Start recording your message"}
            </div>
          )}
        </div>

        {medilistValue !== 2 && (
          <div className="relative group">
            <button
              className={`control-button video-button`}
              onClick={toggleVideo}
              onMouseEnter={() => setShowTooltip(prev => ({ ...prev, video: true }))}
              onMouseLeave={() => setShowTooltip(prev => ({ ...prev, video: false }))}
              onTouchStart={() => handleTouchTooltip('video', true)}
              onTouchEnd={() => handleTouchTooltip('video', false)}
            >
              <FontAwesomeIcon
                icon={isVideoActive ? faVideo : faVideoSlash}
                className="icon"
              />
              <span className="label">Body Language</span>
            </button>
            {showTooltip.video && (
              <div className="custom-tooltip">
                {isVideoActive ? "Disable body language analysis" : "Enable body language analysis"}
              </div>
            )}
          </div>
        )}

        <div className="relative group">
          <button
            className="control-button stop-button"
            onClick={handleEndConversation}
            disabled={isConversationEnded}
            onMouseEnter={() => setShowTooltip(prev => ({ ...prev, stop: true }))}
            onMouseLeave={() => setShowTooltip(prev => ({ ...prev, stop: false }))}
            onTouchStart={() => handleTouchTooltip('stop', true)}
            onTouchEnd={() => handleTouchTooltip('stop', false)}
          >
            <FontAwesomeIcon
              icon={faCommentSlash}
              className="icon"
            />
            <span className="label">End Dialogue</span>
          </button>
          {showTooltip.stop && (
            <div className="custom-tooltip">
              End the conversation and request mentor report
            </div>
          )}
        </div>
      </div>



      <div className="mt-4">
        <label className="text-white mr-2">Speed:</label>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={playbackRate}
          onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
        />
        <span className="text-white ml-2">{playbackRate}x</span>
      </div>

      <WebcamCapture isRecording={isRecording && isVideoActive} conversationId={conversation_id} />
      <SilentAudioWarningModal
        isOpen={showSilentAudioWarning}
        onClose={handleCloseSilentWarning}
      />
      <ConfirmEndModal
        isOpen={showConfirmEndModal}
        onClose={() => setShowConfirmEndModal(false)}
        onConfirm={confirmEndConversation}
      />
      <TimeWarningModal
        isOpen={showTimeWarning}
        onClose={() => setShowTimeWarning(false)}
      />
    </div>
  );
};

export default EnhancedAudioRecorder;
